import React, { useCallback, useEffect, useState } from "react";
import { FiVideo } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import VideoProviderSelector from "./VideoProviderSelector";
import ZoomAuthSection from "./AuthSection/ZoomAuthSection";
import GoogleAuthSection from "./AuthSection/GoogleAuthSection";
import MeetingSection from "./MeetingSection";

import { getProvider } from "./Providers";
import ZoomServices from "../../services/ZoomServices";
import GoogleMeetServices from "../../services/GoogleMeetService";

import "../../assets/css/VideoConference.css";

const PROVIDER_LEGEND = [
  { key: "zoom", label: "Zoom" },
  { key: "google", label: "Google Meet" },
];

export default function VideoConference() {
  const navigate = useNavigate();

  const [provider, setProvider] = useState("zoom");
  const [connectedMap, setConnectedMap] = useState({
    zoom: false,
    google: false,
  });
  const [meetingCounts, setMeetingCounts] = useState({ zoom: 0, google: 0 });

  const refreshConnection = useCallback(async (providerKey) => {
    try {
      const token = await getProvider(providerKey).getValidAccessToken();
      setConnectedMap((prev) => ({ ...prev, [providerKey]: !!token }));
      return token;
    } catch {
      setConnectedMap((prev) => ({ ...prev, [providerKey]: false }));
      return null;
    }
  }, []);

  const refreshMeetingCount = useCallback(async (providerKey) => {
    try {
      const meetings = await getProvider(providerKey).getUpcomingMeetings();
      setMeetingCounts((prev) => ({ ...prev, [providerKey]: meetings?.length || 0 }));
    } catch {
      setMeetingCounts((prev) => ({ ...prev, [providerKey]: 0 }));
    }
  }, []);

  useEffect(() => {
    const appToken = localStorage.getItem("accessToken");
    if (!appToken) {
      navigate("/signin");
      return;
    }

    (async () => {
      for (const key of ["zoom", "google"]) {
        const token = await refreshConnection(key);
        if (token) {
          try {
            await getProvider(key).deleteExpiredMeetings(token);
          } catch (err) {
            console.error(`Failed to delete expired ${key} meetings`, err);
          }
          refreshMeetingCount(key);
        }
      }
    })();

    const handler = async (e) => {
      if (e.data?.type === "ZOOM_OAUTH_SUCCESS") {
        const token = await refreshConnection("zoom");

        if (token) {
          try {
            await getProvider("zoom").deleteExpiredMeetings(token);
          } catch (err) {
            console.error("Failed to delete expired Zoom meetings", err);
          }
          refreshMeetingCount("zoom");
        }
      }

      if (e.data?.type === "GOOGLE_OAUTH_SUCCESS") {
        const token = await refreshConnection("google");

        if (token) {
          try {
            await getProvider("google").deleteExpiredMeetings();
          } catch (err) {
            console.error("Failed to delete expired Google Meet meetings", err);
          }
          refreshMeetingCount("google");
        }
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [navigate, refreshConnection, refreshMeetingCount]);

  useEffect(() => {
    if (connectedMap[provider]) refreshMeetingCount(provider);
  }, [provider, connectedMap, refreshMeetingCount]);

  const isSelectedConnected = connectedMap[provider];

  const disconnectZoom = async () => {
    try {
      await ZoomServices.disconnect();
    } finally {
      setConnectedMap((prev) => ({ ...prev, zoom: false }));
    }
  };

  const disconnectGoogle = async () => {
    try {
      await GoogleMeetServices.disconnect();
    } finally {
      setConnectedMap((prev) => ({ ...prev, google: false }));
    }
  };

  return (
    <div className="video-conference-wrapper">
      <div className="video-conference-header">
        <div>
          <h5 className="video-conference-title page-title">
            <FiVideo /> Video Conference
          </h5>
          <p className="page-subtitle">
            Schedule and manage meetings across multiple platforms
          </p>
        </div>

        <div className="video-provider-legend">
          {PROVIDER_LEGEND.map((p) => (
            <span key={p.key} className={`video-legend-pill video-legend-${p.key}`}>
              <span className="video-legend-dot" />
              {p.label}
            </span>
          ))}
        </div>
      </div>

      <VideoProviderSelector
        provider={provider}
        setProvider={setProvider}
        meetingCounts={meetingCounts}
      />

      <div className="video-conference-body">
        {provider === "zoom" && (
          <ZoomAuthSection connected={connectedMap.zoom} onDisconnect={disconnectZoom} />
        )}
        {provider === "google" && (
          <GoogleAuthSection connected={connectedMap.google} onDisconnect={disconnectGoogle} />
        )}

        {isSelectedConnected && <MeetingSection providerKey={provider} />}
      </div>
    </div>
  );
}
